import { useEffect, useMemo, useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, InfoCircle, Refresh, Trash } from 'iconsax-reactjs';
import {
  FIB_TIMEFRAMES,
  FIB_VISIBLE_BARS,
  TIMEFRAME_LABEL,
  fibLevels,
  retracementOf,
  swingDirection,
  swingInvalidation,
  swingRange,
  timeframeShort,
  type AutoSwing,
  type TeachingFib,
  type Timeframe,
} from '@pipyar/core';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { FibChart, type ChartPoint } from '@/components/chart/FibChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { chartPalette } from '@/components/chart/chartTheme';
import type { FibDrawing } from '@/components/chart/fibPrimitive';
import {
  resolveSwing,
  useAutoSwings,
  useFibCandles,
  useManualSwings,
  useTeachingFibMutations,
  useTeachingFibs,
  type ManualSwingEntry,
} from './api';

/** Stored (broker-clock) time as dd/mm hh:mm, read as UTC like the chart axis. */
function formatStored(time: number): string {
  const d = new Date(time * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * The Fibonacci module.
 *
 * One chart per timeframe, stacked M1 → D1. Each carries two kinds of draw:
 *
 * - the owner's TEACHING draws made on that timeframe with the draw tool,
 *   kept with a note and never removed automatically — worked examples of
 *   which swing the rules mean;
 * - my swing for that timeframe, picked by the rules as taught so far and
 *   removed once price passes its start or end.
 *
 * Separate charts rather than one combined (tried both): the owner reads
 * each timeframe's draw on its own candles. The draw tool snaps every click
 * to the nearer wick of the bar under the pointer.
 */
export function FibonacciPage() {
  const { theme } = useTheme();
  const { symbol } = useSymbol();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: ['fib-candles'] }) > 0;
  const palette = chartPalette(theme);

  const [showMine, setShowMine] = useState(true);
  const [showYours, setShowYours] = useState(true);
  const [showHand, setShowHand] = useState(false);
  const [showPivots, setShowPivots] = useState(true);

  const manual = useManualSwings();
  const auto = useAutoSwings(symbol ?? null);
  const teaching = useTeachingFibs(symbol ?? null);
  const mutations = useTeachingFibMutations();

  const error = [manual, teaching, auto].find((q) => q.error)?.error;
  const digits = 2;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('fib.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('fib.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>
        <span className="ms-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <Toggle checked={showYours} onChange={setShowYours} label={t('fib.showYours')} swatch={palette.fibTeaching} />
          <Toggle checked={showMine} onChange={setShowMine} label={t('fib.showMine')} />
          <Toggle checked={showPivots} onChange={setShowPivots} label={t('fib.showPivots')} />
          <Toggle checked={showHand} onChange={setShowHand} label={t('fib.showHand')} />
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.refresh')}
          onClick={() => {
            void queryClient.invalidateQueries({ queryKey: ['fib-candles'] });
            void queryClient.invalidateQueries({ queryKey: ['fib-manual'] });
            void queryClient.invalidateQueries({ queryKey: ['fib-teaching'] });
            void queryClient.invalidateQueries({ queryKey: ['fib-auto'] });
          }}
        >
          <Refresh size={18} className={fetching ? 'animate-spin' : undefined} />
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : t('fib.error')}</span>
        </div>
      )}

      {FIB_TIMEFRAMES.map((tf) => (
        <FibChartSection
          key={tf}
          symbol={symbol ?? null}
          timeframe={tf}
          entry={symbol ? (manual.data?.[symbol]?.[tf] ?? null) : null}
          auto={auto.data?.swings[tf] ?? null}
          autoPending={auto.isPending}
          teaching={(teaching.data?.fibs ?? []).filter((f) => f.timeframe === tf)}
          showMine={showMine}
          showYours={showYours}
          showHand={showHand}
          showPivots={showPivots}
          onCreate={(start, end) =>
            symbol && mutations.create.mutate({ symbol, timeframe: tf, start, end, note: '' })
          }
        />
      ))}

      <section className="glass rounded-2xl p-3">
        <h2 className="text-sm font-medium">{t('fib.yourDraws')}</h2>
        <p className="mb-2 text-[10px] leading-snug text-muted-foreground">{t('fib.yourDrawsNote')}</p>
        {teaching.isPending ? (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        ) : (teaching.data?.fibs.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground">{t('fib.noDraws')}</p>
        ) : (
          <ul className="space-y-2">
            {teaching.data!.fibs.map((f) => (
              <TeachingRow
                key={f.id}
                fib={f}
                digits={digits}
                onNote={(note) => mutations.update.mutate({ id: f.id, patch: { note } })}
                onDelete={() => mutations.remove.mutate(f.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** One timeframe's chart: its candles, my swing, the owner's draws on it, and the draw tool. */
function FibChartSection({
  symbol,
  timeframe,
  entry,
  auto,
  autoPending,
  teaching,
  showMine,
  showYours,
  showHand,
  showPivots,
  onCreate,
}: {
  symbol: string | null;
  timeframe: Timeframe;
  entry: ManualSwingEntry | null;
  auto: AutoSwing | null;
  autoPending: boolean;
  teaching: TeachingFib[];
  showMine: boolean;
  showYours: boolean;
  showHand: boolean;
  showPivots: boolean;
  onCreate: (start: ChartPoint, end: ChartPoint) => void;
}) {
  const { theme } = useTheme();
  const { t } = useI18n();
  const palette = chartPalette(theme);
  const color = palette.fib[timeframe] ?? palette.text;

  const query = useFibCandles(symbol, timeframe);
  const data = query.data;
  const digits = data?.digits ?? 2;

  // The forming bar is appended so the chart's last candle is live; useLiveBar
  // then moves it tick by tick.
  const candles = useMemo(() => {
    const closed = data?.candles ?? [];
    return data?.live ? [...closed, data.live] : closed;
  }, [data]);

  // The hand-picked swing from manual.json, kept for comparison with the
  // detector. A running end is resolved against these candles.
  const hand = useMemo(() => (entry ? resolveSwing(entry, candles) : null), [entry, candles]);
  const handInvalid = useMemo(
    () => (hand && candles.length > 0 ? swingInvalidation(hand, candles) : null),
    [hand, candles],
  );

  // The detector's swing. Its removal was judged by the API on the same bars.
  const swing = auto && !auto.invalid ? auto : null;
  const running = Boolean(auto?.running);

  const lastClose = candles.length > 0 ? candles[candles.length - 1]!.close : null;
  const retracement = swing && lastClose !== null ? retracementOf(swing, lastClose) : null;

  // ---- draw mode ----
  const [drawing, setDrawing] = useState(false);
  const [pendingStart, setPendingStart] = useState<ChartPoint | null>(null);
  const [hover, setHover] = useState<ChartPoint | null>(null);

  const stopDrawing = () => {
    setDrawing(false);
    setPendingStart(null);
    setHover(null);
  };

  const onPick = (point: ChartPoint) => {
    if (!pendingStart) {
      setPendingStart(point);
      return;
    }
    if (point.time === pendingStart.time && point.price === pendingStart.price) return;
    onCreate(pendingStart, point);
    setPendingStart(null);
    setHover(null);
  };

  useEffect(() => {
    if (!drawing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopDrawing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawing]);

  const drawings = useMemo<FibDrawing[]>(() => {
    const out: FibDrawing[] = [];
    if (showMine && swing) {
      out.push({
        tag: timeframeShort(timeframe),
        color,
        direction: swing.direction,
        start: swing.start,
        end: swing.end,
        levels: swing.levels,
        digits,
        path: showPivots ? swing.pivots.map((p) => ({ time: p.time, price: p.price })) : undefined,
      });
    }
    if (showHand && hand && !handInvalid) {
      out.push({
        tag: `${timeframeShort(timeframe)} ${t('fib.hand')}`,
        // Translucent: the detector's draw is the one to read; this is the reference.
        color: `${color}80`,
        direction: hand.direction,
        start: hand.start,
        end: hand.end,
        levels: fibLevels(hand),
        digits,
      });
    }
    if (showYours) {
      for (const f of teaching) {
        out.push({
          tag: `T${f.id}`,
          color: palette.fibTeaching,
          direction: f.direction,
          start: f.start,
          end: f.end,
          levels: fibLevels(f),
          digits,
        });
      }
    }
    if (pendingStart && hover && hover.time !== pendingStart.time) {
      const s = { direction: swingDirection(pendingStart, hover), start: pendingStart, end: hover };
      out.push({ tag: 'new', color: palette.fibTeaching, ...s, levels: fibLevels(s), digits });
    }
    return out;
  }, [showMine, showYours, showHand, showPivots, swing, hand, handInvalid, teaching, pendingStart, hover, timeframe, color, palette, digits, t]);

  const drawButton = (compact: boolean) => (
    <>
      <button
        type="button"
        onClick={() => (drawing ? stopDrawing() : setDrawing(true))}
        aria-pressed={drawing}
        className={cn(
          'rounded-lg font-medium transition-colors',
          compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1 text-xs',
          drawing ? 'bg-primary text-primary-foreground' : 'bg-elevated text-muted-foreground hover:text-foreground',
        )}
      >
        {drawing ? t('fib.cancel') : t('fib.draw')}
      </button>
      {drawing && (
        <span className="text-xs text-muted-foreground">
          {pendingStart ? t('fib.drawEnd') : t('fib.drawStart')}
        </span>
      )}
    </>
  );

  const label = `${timeframeShort(timeframe)} · ${TIMEFRAME_LABEL[timeframe]}`;

  return (
    // min-w-0: see KnotsPage — without it the section cannot shrink back after
    // a full-screen pass and the page scrolls sideways.
    <section className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-block h-0.5 w-3" style={{ backgroundColor: color }} aria-hidden="true" />
        <h2 className="text-sm font-semibold">{timeframeShort(timeframe)}</h2>
        <span className="text-muted-foreground">{TIMEFRAME_LABEL[timeframe]}</span>

        {auto?.invalid && (
          <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
            {t(auto.invalid.passed === 'start' ? 'fib.removedStart' : 'fib.removedEnd', {
              price: auto.invalid.price.toFixed(digits),
              time: formatStored(auto.invalid.time),
            })}
          </span>
        )}
        {!auto && !autoPending && <span className="text-muted-foreground">{t('fib.autoNone')}</span>}

        {swing && (
          <span className="flex items-center gap-1.5">
            <span
              className={cn(
                'flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium',
                swing.direction === 'up' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
              )}
            >
              {swing.direction === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {t(swing.direction === 'up' ? 'fib.swingUp' : 'fib.swingDown')}
            </span>
            <span className="tabular text-muted-foreground">
              {swing.start.price.toFixed(digits)} → {swing.end.price.toFixed(digits)} ({swingRange(swing).toFixed(digits)})
            </span>
            {running && <span className="rounded bg-elevated px-1.5 py-0.5 text-muted-foreground">{t('fib.running')}</span>}
            <span className="text-muted-foreground">{t('fib.reversal', { pct: (swing.reversalPct * 100).toFixed(1) })}</span>
            {(swing.start.refinedOn || swing.end.refinedOn) && (
              <span className="text-muted-foreground">
                {t('fib.refinedOn', {
                  tfs: [swing.start.refinedOn, swing.end.refinedOn].filter(Boolean).map((x) => timeframeShort(x!)).join('/'),
                })}
              </span>
            )}
            {swing.score && (
              <span className="rounded bg-elevated px-1.5 py-0.5 text-muted-foreground">
                {t('fib.score', { hits: swing.score.hits })}
              </span>
            )}
          </span>
        )}

        {retracement !== null && (
          <span className="tabular">
            {t('fib.retraced')}{' '}
            <span
              className={cn(
                'font-semibold',
                retracement > 1 ? 'text-bear' : retracement < 0 ? 'text-bull' : undefined,
              )}
            >
              {Math.round(retracement * 1000) / 10}%
            </span>
          </span>
        )}

        <span className="ms-auto flex items-center gap-2">
          {drawButton(false)}
          <span className="text-muted-foreground tabular">
            {data ? t('fib.barsLoaded', { count: data.count }) : ''}
          </span>
        </span>
      </div>

      {query.error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{query.error instanceof Error ? query.error.message : t('fib.error')}</span>
        </div>
      )}

      <ChartFrame
        label={label}
        fill={false}
        fullscreenToolbar={drawButton(true)}
        className="group/frame h-[30rem] overflow-hidden glass rounded-2xl"
      >
        {query.isPending ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : candles.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('fib.noCandles', { timeframe: timeframeShort(timeframe) })}
          </div>
        ) : (
          <FibChart
            className="h-full w-full"
            candles={candles}
            drawings={drawings}
            visibleBars={FIB_VISIBLE_BARS[timeframe]}
            digits={digits}
            theme={theme}
            resetKey={`${symbol}-${timeframe}`}
            drawMode={drawing}
            onPick={onPick}
            onHover={setHover}
          />
        )}
      </ChartFrame>

      {showHand && entry?.note && (
        <p className="text-xs leading-snug text-muted-foreground">
          <span className="font-medium">{t('fib.hand')}:</span> {entry.note}
        </p>
      )}
    </section>
  );
}

function TeachingRow({
  fib,
  digits,
  onNote,
  onDelete,
}: {
  fib: TeachingFib;
  digits: number;
  onNote: (note: string) => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState(fib.note);
  useEffect(() => setNote(fib.note), [fib.note]);

  return (
    <li className="space-y-1 rounded-lg bg-elevated/60 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold">T{fib.id}</span>
        <span className="rounded bg-muted px-1.5 py-0.5">{timeframeShort(fib.timeframe)}</span>
        <span
          className={cn(
            'flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium',
            fib.direction === 'up' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
          )}
        >
          {fib.direction === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {t(fib.direction === 'up' ? 'fib.swingUp' : 'fib.swingDown')}
        </span>
        <span className="tabular text-muted-foreground">
          {formatStored(fib.start.time)} {fib.start.price.toFixed(digits)} → {formatStored(fib.end.time)}{' '}
          {fib.end.price.toFixed(digits)} ({swingRange(fib).toFixed(digits)})
        </span>
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('common.delete')}
          className="ms-auto rounded p-1 text-muted-foreground hover:text-danger"
        >
          <Trash size={14} />
        </button>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note !== fib.note) onNote(note);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        placeholder={t('fib.notePlaceholder')}
        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs outline-none placeholder:text-muted-foreground/60 focus-visible:border-ring"
      />
    </li>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  swatch,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  swatch?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 rounded border-border accent-[var(--primary)]"
      />
      {swatch && <span className="inline-block h-0.5 w-3" style={{ backgroundColor: swatch }} aria-hidden="true" />}
      {label}
    </label>
  );
}
